import { stringLength } from "@-/com.npmjs.string-length";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import type { MorphemeGlyphTileGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import type {
  GlyphStyle,
  MorphemeGlyphTileCompletion,
  MorphemeGlyphTileStage,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import type { AppSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { GraphemeMorphemeGlyphTileLink } from "#grapheme-morpheme-glyph-tile-link";
import { WordMorphemeGlyphTileLink } from "#word-morpheme-glyph-tile-link";

export type MorphemeGlyphTileLinkProps = {
  className?: string;
  completion?: MorphemeGlyphTileCompletion;
  glyphStyle?: GlyphStyle;
  href: string;
  /**
   * Whether this grapheme's card isn't version-complete yet — forwarded to the grapheme tile so it
   * fades under the `fadeMissingMnemonicEnabled` setting. Resolved from a batched per-grid completion
   * read. Grapheme-only — ignored for words (their completion isn't keyed by a single grapheme file).
   */
  isMissingMnemonic?: boolean;
  morpheme: string | GraphemeGlyph;
  /**
   * Pre-resolved grapheme pinyin slice from a batched parent query, forwarded to the grapheme link
   * button. Paired with `stage`, the tile fires no per-tile queries (fast path for large grids).
   * Grapheme-only — ignored for words.
   */
  morphemeGlyphTileSlice?: MorphemeGlyphTileGraphemeMorphemeSlice;
  /**
   * Navigation override forwarded to the grapheme tile link. When set, the tap calls this with
   * the resolved morpheme instead of the generic panels-navigator push, so callers can route to a
   * destination with its own richer prefetch (e.g. the Learn review card). `href`/`prefixParams`/
   * `view` are ignored when set.
   */
  onNavigate?: (morpheme: string) => void;
  prefixParams?: MorphemeHrefObject["params"];
  /** Selectable leaves represented by the tile. Defaults to one morpheme item. */
  selectionItems?: readonly AppSelectionItem[];
  /** Grapheme pronunciation cards show their own reading above the glyph. */
  showPrimaryPinyin?: boolean;
  stage?: MorphemeGlyphTileStage;
  uncolored?: boolean;
  /**
   * Which panel to open the target card on. Defaults to `"mnemonics"` (glyph-grid behavior).
   * Forwarded to the grapheme/word tile links.
   */
  view?: "mnemonics" | "info";
};

export function MorphemeGlyphTileLink({
  className,
  completion,
  glyphStyle,
  href,
  isMissingMnemonic,
  morpheme,
  morphemeGlyphTileSlice,
  onNavigate,
  prefixParams,
  selectionItems,
  showPrimaryPinyin,
  stage,
  uncolored,
  view,
}: MorphemeGlyphTileLinkProps) {
  if (typeof morpheme !== "string") {
    return (
      <GraphemeMorphemeGlyphTileLink
        className={className}
        completion={completion}
        glyph={morpheme}
        glyphStyle={glyphStyle}
        href={href}
        isMissingMnemonic={isMissingMnemonic}
        morphemeGlyphTileSlice={morphemeGlyphTileSlice}
        onNavigate={onNavigate}
        prefixParams={prefixParams}
        selectionItems={selectionItems}
        showPrimaryPinyin={showPrimaryPinyin}
        stage={stage}
        uncolored={uncolored}
        view={view}
      />
    );
  }

  if (stringLength(morpheme) === 1) {
    return (
      <GraphemeMorphemeGlyphTileLink
        className={className}
        completion={completion}
        glyph={{ color: null, display: morpheme, fontWeight: "400", original: morpheme }}
        glyphStyle={glyphStyle}
        href={href}
        isMissingMnemonic={isMissingMnemonic}
        morphemeGlyphTileSlice={morphemeGlyphTileSlice}
        onNavigate={onNavigate}
        prefixParams={prefixParams}
        selectionItems={selectionItems}
        showPrimaryPinyin={showPrimaryPinyin}
        stage={stage}
        uncolored={uncolored}
        view={view}
      />
    );
  }

  return (
    <WordMorphemeGlyphTileLink
      className={className}
      completion={completion}
      glyphStyle={glyphStyle}
      href={href}
      onNavigate={onNavigate}
      prefixParams={prefixParams}
      selectionItems={selectionItems}
      stage={stage}
      uncolored={uncolored}
      view={view}
      word={morpheme}
    />
  );
}
